<script setup>
import { ref, onMounted, watch } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import axios from 'axios';

const props = defineProps({
    contactId: { type: [Number, String], required: true },
});

const submissions = ref([]);
const isLoading = ref(false);
const expandedId = ref(null);
const accountId = useMapGetter('getCurrentAccountId');

async function fetchSubmissions() {
    if (!props.contactId) return;
    isLoading.value = true;
    try {
        const url = `/api/v1/accounts/${accountId.value}/contacts/${props.contactId}/form_submissions`;
        const { data } = await axios.get(url);
        submissions.value = data.data || [];
    } catch {
        submissions.value = [];
    } finally {
        isLoading.value = false;
    }
}

function toggleExpand(id) {
    expandedId.value = expandedId.value === id ? null : id;
}

function formatDate(dateStr) {
    return new Date(dateStr).toLocaleString('pt-BR', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
    });
}

function getPurposeLabel(purpose) {
    return purpose === 'information_collection' ? 'Coleta' : 'Lead';
}

function getPurposeClasses(purpose) {
    return purpose === 'information_collection'
        ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'
        : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
}

onMounted(fetchSubmissions);
watch(() => props.contactId, fetchSubmissions);
</script>

<template>
    <div>
        <div v-if="isLoading" class="flex justify-center py-3">
            <span class="animate-spin i-lucide-loader-2 size-4 text-n-slate-10" />
        </div>

        <div v-else-if="submissions.length === 0" class="py-3 text-center">
            <span class="i-lucide-file-text size-6 text-n-slate-8 mx-auto block mb-1" />
            <p class="text-xs text-n-slate-10">Nenhum formulário preenchido.</p>
        </div>

        <div v-else class="flex flex-col gap-2">
            <div v-for="sub in submissions" :key="sub.id"
                class="rounded-lg border border-n-weak bg-n-solid-1 overflow-hidden transition-all"
                :class="{ 'ring-1 ring-n-brand/20': expandedId === sub.id }">
                <button class="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-n-alpha-1 transition"
                    @click="toggleExpand(sub.id)">
                    <span class="i-lucide-file-check size-3.5 text-n-slate-10 flex-shrink-0" />
                    <div class="flex-1 min-w-0">
                        <span class="text-xs font-medium text-n-slate-12 truncate block">
                            {{ sub.form_name || 'Formulário' }}
                        </span>
                        <span class="text-[10px] text-n-slate-10">{{ formatDate(sub.created_at) }}</span>
                    </div>
                    <span class="px-1.5 py-0.5 rounded text-[9px] font-medium flex-shrink-0"
                        :class="getPurposeClasses(sub.form_purpose)">
                        {{ getPurposeLabel(sub.form_purpose) }}
                    </span>
                    <span class="transition-transform duration-200 i-lucide-chevron-down size-3 text-n-slate-10"
                        :class="{ 'rotate-180': expandedId === sub.id }" />
                </button>

                <div v-if="expandedId === sub.id" class="px-3 pb-3 pt-1 border-t border-n-weak">
                    <div v-for="(value, key) in sub.data" :key="key" class="flex gap-2 py-1.5 border-b border-n-weak/50 last:border-0">
                        <span class="text-[11px] font-medium text-n-slate-10 min-w-[80px] capitalize">{{ key }}</span>
                        <span class="text-[11px] text-n-slate-12 break-all">{{ value || '—' }}</span>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
